import { createSlice, PayloadAction } from "@reduxjs/toolkit";
import type { UserAuthAndAdminSliceState } from "./types/redux";

const STORAGE_KEY = 'dealos_custom_auth';

interface StoredAuth {
  userId: string | null;
  userEmail: string | null;
  userName: string | null;
  userRoles: string[] | readonly string[];
  isAdmin: boolean;
  accessToken: string | null;
  authType: 'msal' | 'custom' | null;
  isAuthenticated: boolean;
}

const loadFromStorage = (): StoredAuth | null => {
  try {
    const stored = localStorage.getItem(STORAGE_KEY);
    if (stored) {
      const parsed: any = JSON.parse(stored);
      return {
        ...parsed,
        isAuthenticated: true,
      } as StoredAuth;
    }
  } catch (_error) {
    localStorage.removeItem(STORAGE_KEY);
  }
  return null;
};

const saveToStorage = (state: UserAuthAndAdminSliceState): void => {
  try {
    const toStore = {
      userId: state.userId,
      userEmail: state.userEmail,
      userName: state.userName,
      userRoles: state.userRoles,
      isAdmin: state.isAdmin,
      accessToken: state.accessToken,
      authType: state.authType,
    };
    localStorage.setItem(STORAGE_KEY, JSON.stringify(toStore));
  } catch (_error) {
    // Silent error - localStorage might be unavailable
  }
};

const clearStorage = (): void => {
  try {
    localStorage.removeItem(STORAGE_KEY);
  } catch (_error) {
    // Silent error - localStorage might be unavailable
  }
};

const storedAuth = loadFromStorage();

const initialState: UserAuthAndAdminSliceState = storedAuth ? {
  userId: storedAuth.userId,
  userEmail: storedAuth.userEmail,
  userName: storedAuth.userName,
  userRoles: Array.isArray(storedAuth.userRoles) ? storedAuth.userRoles : [],
  isAdmin: storedAuth.isAdmin,
  isAuthenticated: storedAuth.isAuthenticated,
  accessToken: storedAuth.accessToken,
  authType: storedAuth.authType,
} : {
  userId: null,
  userEmail: null,
  userName: null,
  userRoles: [],
  isAdmin: false,
  isAuthenticated: false,
  accessToken: null,
  authType: null,  // Track whether auth is 'msal' or 'custom'
};

interface SetUserInfoPayload {
  account: {
    localAccountId?: string;
    homeAccountId?: string;
    username: string;
    name?: string;
    idTokenClaims?: {
      roles?: string[];
      groups?: string[];
    };
  };
  accessToken: string;
}

const userAuthAndAdminSlice = createSlice({
  name: "userAuthAndAdminSlice",
  initialState,
  reducers: {
    setUserInfo: (state, action: PayloadAction<SetUserInfoPayload>) => {
      const { account, accessToken } = action.payload;

      // All auth uses MSAL-style format for consistency

      state.userId = account.localAccountId || account.homeAccountId || null;
      state.userEmail = account.username;
      state.userName = account.name || account.username || null;
      state.isAuthenticated = true;
      state.accessToken = accessToken;

      // Determine auth type based on whether it's a real MSAL account or custom auth
      // Real MSAL accounts have complex localAccountId formats, custom auth uses simple usernames
      const isCustomAuth = !account.localAccountId?.includes('.') && !account.localAccountId?.includes('-');
      state.authType = isCustomAuth ? "custom" : "msal";

      if (account.idTokenClaims) {
        const roles = account.idTokenClaims.roles || [];
        state.userRoles = Array.isArray(roles) ? roles : [];

        // Check for admin status via Azure AD group membership
        const adminGroupGuid = import.meta.env.VITE_ADMIN_GROUP_GUID?.trim();
        const groups = account.idTokenClaims.groups || [];
        const groupsArray = Array.isArray(groups) ? groups : [];
        state.isAdmin = adminGroupGuid ? groupsArray.includes(adminGroupGuid) : false;
      }

      // Save custom auth to localStorage, but not MSAL auth (MSAL handles its own persistence)
      if (isCustomAuth) {
        saveToStorage(state);
      }
    },
    clearUserInfo: (state) => {
      state.userId = null;
      state.userEmail = null;
      state.userName = null;
      state.userRoles = [];
      state.isAdmin = false;
      state.isAuthenticated = false;
      state.accessToken = null;
      state.authType = null;

      // Clear custom auth from localStorage
      clearStorage();
    },
    setAdminStatus: (state, action: PayloadAction<boolean>) => {
      state.isAdmin = action.payload;
    },
  },
});

export const { setUserInfo, clearUserInfo, setAdminStatus } = userAuthAndAdminSlice.actions;

export default userAuthAndAdminSlice.reducer;
